/**
 * Harta de acoperire din survey: interpolare IDW (inverse distance weighting)
 * pe o grila in metri, cu MASCA. Functii pure, scrise aici - fara biblioteca.
 *
 * IDW: valoarea intr-un punct e media valorilor celor mai apropiate `k`
 * masuratori, fiecare cu greutatea 1 / d^p. Simplu si cinstit: nu scoate
 * niciodata o valoare mai buna decat cea mai buna masuratoare, si nici mai
 * rea decat cea mai rea.
 *
 * MASCA: IDW da o valoare peste tot, si departe de orice masuratoare valoarea
 * aia e o ghicitoare. Se coloreaza deci numai celulele aflate la cel mult
 * `maskM` metri de un punct masurat; restul ramane gri. Legenda o spune:
 * "interpolated between measured spots".
 */

export type Sample = { x: number; y: number; v: number }

export type Thresholds = { good: number; fair: number; poor: number }

export type Grid = {
  cols: number
  rows: number
  /** Latura celulei, in metri. */
  cell: number
  /** Valoarea fiecarei celule (rand dupa rand, de sus), NaN = in afara mastii. */
  values: Float32Array
}

export const DEFAULT_K = 6
export const DEFAULT_POWER = 2

/** Cea mai mica distanta de la (x, y) la o masuratoare, in metri. */
export function nearestDistance(samples: Sample[], x: number, y: number): number {
  let best = Infinity
  for (const s of samples) {
    const d = Math.hypot(s.x - x, s.y - y)
    if (d < best) best = d
  }
  return best
}

/**
 * IDW cu cei mai apropiati `k` vecini. `null` fara masuratori. Pe un punct
 * masurat intoarce exact valoarea lui.
 */
export function idw(samples: Sample[], x: number, y: number, k = DEFAULT_K, power = DEFAULT_POWER): number | null {
  if (samples.length === 0) return null
  const n = Math.max(1, Math.min(k, samples.length))
  /* Cei mai apropiati n, tinuti ordonati intr-un tablou mic (n e mic). */
  const nd: number[] = []
  const nv: number[] = []
  for (const s of samples) {
    const d2 = (s.x - x) * (s.x - x) + (s.y - y) * (s.y - y)
    if (d2 < 1e-12) return s.v
    if (nd.length === n && d2 >= nd[n - 1]) continue
    let i = nd.length === n ? n - 1 : nd.length
    if (nd.length < n) {
      nd.push(d2)
      nv.push(s.v)
    }
    while (i > 0 && nd[i - 1] > d2) {
      nd[i] = nd[i - 1]
      nv[i] = nv[i - 1]
      i--
    }
    nd[i] = d2
    nv[i] = s.v
  }
  let wsum = 0
  let vsum = 0
  for (let i = 0; i < nd.length; i++) {
    const w = 1 / Math.pow(Math.sqrt(nd[i]), power)
    wsum += w
    vsum += w * nv[i]
  }
  return vsum / wsum
}

/**
 * Grila de acoperire peste un plan de `widthM` x `heightM` metri. Centrul
 * celulei decide: in masca daca e la cel mult `maskM` de o masuratoare.
 */
export function coverageGrid(samples: Sample[], widthM: number, heightM: number, cellM: number, maskM: number, k = DEFAULT_K): Grid {
  const cols = Math.max(1, Math.ceil(widthM / cellM))
  const rows = Math.max(1, Math.ceil(heightM / cellM))
  const values = new Float32Array(cols * rows).fill(Number.NaN)
  if (samples.length === 0) return { cols, rows, cell: cellM, values }
  for (let r = 0; r < rows; r++) {
    const y = (r + 0.5) * cellM
    for (let c = 0; c < cols; c++) {
      const x = (c + 0.5) * cellM
      if (nearestDistance(samples, x, y) > maskM) continue
      const v = idw(samples, x, y, k)
      if (v !== null) values[r * cols + c] = v
    }
  }
  return { cols, rows, cell: cellM, values }
}

export type Rgba = [number, number, number, number]

/** Culorile treptelor. Aceleasi in legenda (FloorPlan.css le repeta). */
export const STEP_COLORS: { good: Rgba; fair: Rgba; poor: Rgba; bad: Rgba; masked: Rgba } = {
  good: [46, 160, 67, 150],
  fair: [210, 190, 40, 150],
  poor: [235, 130, 30, 150],
  bad: [210, 45, 45, 150],
  masked: [120, 120, 120, 70],
}

/** Treapta unei valori: >= good, >= fair, >= poor, sub poor. */
export function step(v: number, t: Thresholds): 'good' | 'fair' | 'poor' | 'bad' {
  if (v >= t.good) return 'good'
  if (v >= t.fair) return 'fair'
  if (v >= t.poor) return 'poor'
  return 'bad'
}

export function colorFor(v: number, t: Thresholds): Rgba {
  return Number.isNaN(v) ? STEP_COLORS.masked : STEP_COLORS[step(v, t)]
}

/** Grila ca pixeli RGBA (o celula = un pixel), pentru un canvas. */
export function gridToRgba(grid: Grid, t: Thresholds): Uint8ClampedArray {
  const out = new Uint8ClampedArray(grid.cols * grid.rows * 4)
  for (let i = 0; i < grid.values.length; i++) {
    const [r, g, b, a] = colorFor(grid.values[i], t)
    out[i * 4] = r
    out[i * 4 + 1] = g
    out[i * 4 + 2] = b
    out[i * 4 + 3] = a
  }
  return out
}
